import { isEmailValid } from "@app/lib/utils";
import { Chip, cn } from "@dust-tt/sparkle";
import type { ClipboardEvent, KeyboardEvent } from "react";
import { useRef } from "react";

const commitKeys = new Set(["Enter", "Tab"]);
const separatorPattern = /[\s,;]/;

export function splitEmails(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((e) => e.trim())
    .filter((e) => e !== "");
}

interface EmailChipsInputProps {
  id?: string;
  emails: string[];
  onEmailsChange: (emails: string[]) => void;
  draft: string;
  onDraftChange: (draft: string) => void;
  error?: string;
}

export function EmailChipsInput({
  id,
  emails,
  onEmailsChange,
  draft,
  onDraftChange,
  error,
}: EmailChipsInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const addEmails = (candidates: string[], nextDraft = "") => {
    const next = [...emails];
    for (const email of candidates) {
      if (!next.some((e) => e.toLowerCase() === email.toLowerCase())) {
        next.push(email);
      }
    }
    onEmailsChange(next);
    onDraftChange(nextDraft);
  };

  const handleChange = (value: string) => {
    if (!separatorPattern.test(value)) {
      onDraftChange(value);
      return;
    }
    const parts = value.split(/[\s,;]+/);
    const trailing = parts.pop() ?? "";
    addEmails(
      parts.map((e) => e.trim()).filter((e) => e !== ""),
      trailing
    );
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = splitEmails(e.clipboardData.getData("text"));
    if (pasted.length > 1) {
      e.preventDefault();
      addEmails([...splitEmails(draft), ...pasted]);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (commitKeys.has(e.key) && draft.trim() !== "") {
      e.preventDefault();
      addEmails(splitEmails(draft));
      return;
    }
    if (e.key === "Backspace" && draft === "" && emails.length > 0) {
      e.preventDefault();
      onEmailsChange(emails.slice(0, -1));
    }
  };

  return (
    <div className="flex flex-col gap-1 p-px">
      <div
        className={cn(
          "flex min-h-24 w-full cursor-text flex-wrap content-start items-center gap-1.5 px-3 py-2 text-sm",
          "rounded-xl border bg-muted-background text-foreground transition duration-100",
          "focus-within:ring-2",
          error
            ? "border-border-warning/30 focus-within:border-border-warning focus-within:ring-warning/10"
            : "border-border focus-within:border-border-focus focus-within:ring-highlight/20"
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {emails.map((email) => (
          <Chip
            key={email}
            size="xs"
            color={isEmailValid(email) ? "primary" : "warning"}
            label={email}
            onRemove={() => onEmailsChange(emails.filter((e) => e !== email))}
          />
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          inputMode="email"
          autoComplete="off"
          className="min-w-40 flex-1 border-none bg-transparent p-0 py-0.5 text-sm outline-hidden placeholder:text-muted-foreground focus:ring-0"
          placeholder={emails.length === 0 ? "name@company.com" : ""}
          value={draft}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => {
            if (draft.trim() !== "") {
              addEmails(splitEmails(draft));
            }
          }}
        />
      </div>
      {error && <div className="ml-2 text-xs text-warning-500">{error}</div>}
    </div>
  );
}
